/**
 * Merina HMS - Accessibility Agent UI
 * =====================================
 * Floating AI-style accessibility assistant.
 * Renders in separate root - ZERO changes to existing components.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import accessibilityEngine from './AccessibilityEngine';
import './accessibility.css';

// Icons as inline SVGs to avoid dependencies
// Wheelchair accessibility icon - Bold, prominent, and eye-catching
const AccessibilityIcon = ({ animate = true }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={animate ? 'a11y-icon-animate' : ''}
  >
    {/* International Wheelchair Symbol - Bold and Clear */}
    {/* Head */}
    <circle cx="12" cy="4" r="2.5" fill="currentColor"/>
    {/* Body and Arms */}
    <path 
      d="M12 7.5c-.83 0-1.5.67-1.5 1.5v4.5h-3c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5h4.5c.83 0 1.5-.67 1.5-1.5V9c0-.83-.67-1.5-1.5-1.5z" 
      fill="currentColor"
    />
    {/* Wheelchair wheel */}
    <circle cx="9" cy="18" r="3.5" fill="none" stroke="currentColor" strokeWidth="2.5"/>
    {/* Leg rest */}
    <path d="M13.5 13.5l3 4.5h2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
  </svg>
);

// Alternative prominent wheelchair icon (standard accessibility symbol)
const WheelchairIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="4" r="2"/>
    <path d="M19 13v-2c0-1.1-.9-2-2-2h-3V7h-2v4H9v2h3v3l-2.8 4.2c-.5.7-.3 1.7.4 2.2.7.5 1.7.3 2.2-.4L15 18h4v-2h-2.5l-1.4-3H17c1.1 0 2-.9 2-2z"/>
    <circle cx="9.5" cy="19.5" r="2.5"/>
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const TextIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
    <path d="M4 7V4h16v3"/>
    <path d="M9 20h6"/>
    <path d="M12 4v16"/>
  </svg>
);

const ContrastIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 2a10 10 0 0 1 0 20"/>
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
    <rect x="6" y="4" width="4" height="16"/>
    <rect x="14" y="4" width="4" height="16"/>
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const LineSpacingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
    <line x1="21" y1="6" x2="3" y2="6"/>
    <line x1="21" y1="12" x2="3" y2="12"/>
    <line x1="21" y1="18" x2="3" y2="18"/>
  </svg>
);

const CursorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
    <path d="M4 4l16 6-6 2 6 8-3 1-6-8-3 5z"/>
  </svg>
);

// Toggle Switch Component
const Toggle = ({ active, onChange, ariaLabel }) => (
  <button
    className={`a11y-toggle ${active ? 'active' : ''}`}
    onClick={onChange}
    role="switch"
    aria-checked={active}
    aria-label={ariaLabel}
  />
);

// Size Button Component
const SizeButton = ({ size, label, active, onClick }) => (
  <button
    className={`a11y-size-btn ${active ? 'active' : ''}`}
    onClick={() => onClick(size)}
    aria-pressed={active}
  >
    {label}
  </button>
);

// Main Agent Component
const AccessibilityAgent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(accessibilityEngine.getSettings());
  const [enabledCount, setEnabledCount] = useState(0);

  // Subscribe to settings changes
  useEffect(() => {
    const unsubscribe = accessibilityEngine.subscribe((newSettings) => {
      setSettings(newSettings);
      updateEnabledCount(newSettings);
    });

    // Initial count - use engine directly to avoid stale closure
    updateEnabledCount(accessibilityEngine.getSettings());

    return unsubscribe;
  }, []);

  // Keyboard shortcut to toggle panel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key === 'a') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const updateEnabledCount = (s) => {
    let count = 0;
    if (s.textSize !== 'md') count++;
    if (s.highContrast) count++;
    if (s.darkMode) count++;
    if (s.reduceMotion) count++;
    if (s.focusMode) count++;
    if (s.readable) count++;
    if (s.dyslexiaFont) count++;
    if (s.cursorSize !== 'normal') count++;
    setEnabledCount(count);
  };

  const handleToggle = useCallback((key) => {
    accessibilityEngine.setSetting(key, !settings[key]);
  }, [settings]);

  const handleTextSize = useCallback((size) => {
    accessibilityEngine.setSetting('textSize', size);
  }, []);

  const handleCursorSize = useCallback((size) => {
    accessibilityEngine.setSetting('cursorSize', size);
  }, []);

  const handleReset = useCallback(() => {
    accessibilityEngine.resetSettings();
    setIsOpen(false);
  }, []);

  const isDarkMode = settings.darkMode || settings.highContrast;

  return (
    <>
      {/* Floating Agent Button */}
      <button
        className="a11y-agent-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open Accessibility Settings"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        data-testid="accessibility-agent-button"
      >
        <AccessibilityIcon />
        {enabledCount > 0 && (
          <span className="badge" aria-label={`${enabledCount} features enabled`}>
            {enabledCount}
          </span>
        )}
        <span className="a11y-tooltip">Accessibility Assistant (Alt+A)</span>
      </button>

      {/* Agent Panel */}
      {isOpen && (
        <div
          className={`a11y-agent-panel ${isDarkMode ? 'dark' : ''}`}
          role="dialog"
          aria-label="Accessibility Settings"
          aria-modal="true"
          data-testid="accessibility-panel"
        >
          {/* Header */}
          <div className="a11y-panel-header">
            <h2>
              <AccessibilityIcon />
              Accessibility
            </h2>
            <button
              className="a11y-panel-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close accessibility panel"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Content */}
          <div className="a11y-panel-content">
            {/* Text Size Section */}
            <div className="a11y-section">
              <div className="a11y-section-title">Text Size</div>
              <div className="a11y-size-selector">
                <SizeButton
                  size="sm"
                  label="A-"
                  active={settings.textSize === 'sm'}
                  onClick={handleTextSize}
                />
                <SizeButton
                  size="md"
                  label="A"
                  active={settings.textSize === 'md'}
                  onClick={handleTextSize}
                />
                <SizeButton
                  size="lg"
                  label="A+"
                  active={settings.textSize === 'lg'}
                  onClick={handleTextSize}
                />
                <SizeButton
                  size="xl"
                  label="A++"
                  active={settings.textSize === 'xl'}
                  onClick={handleTextSize}
                />
              </div>
            </div>

            {/* Visual Section */}
            <div className="a11y-section">
              <div className="a11y-section-title">Visual</div>

              <div className="a11y-toggle-row">
                <span className="a11y-toggle-label">
                  <ContrastIcon />
                  High Contrast
                </span>
                <Toggle
                  active={settings.highContrast}
                  onChange={() => handleToggle('highContrast')}
                  ariaLabel="Toggle high contrast mode"
                />
              </div>

              <div className="a11y-toggle-row">
                <span className="a11y-toggle-label">
                  <MoonIcon />
                  Dark Mode
                </span>
                <Toggle
                  active={settings.darkMode}
                  onChange={() => handleToggle('darkMode')}
                  ariaLabel="Toggle dark mode"
                />
              </div>

              <div className="a11y-toggle-row">
                <span className="a11y-toggle-label">
                  <EyeIcon />
                  Focus Highlight
                </span>
                <Toggle
                  active={settings.focusMode}
                  onChange={() => handleToggle('focusMode')}
                  ariaLabel="Toggle focus highlight"
                />
              </div>
            </div>

            {/* Motion & Readability Section */}
            <div className="a11y-section">
              <div className="a11y-section-title">Motion & Readability</div>

              <div className="a11y-toggle-row">
                <span className="a11y-toggle-label">
                  <PauseIcon />
                  Reduce Motion
                </span>
                <Toggle
                  active={settings.reduceMotion}
                  onChange={() => handleToggle('reduceMotion')}
                  ariaLabel="Toggle reduce motion"
                />
              </div>

              <div className="a11y-toggle-row">
                <span className="a11y-toggle-label">
                  <LineSpacingIcon />
                  Readable Spacing
                </span>
                <Toggle
                  active={settings.readable}
                  onChange={() => handleToggle('readable')}
                  ariaLabel="Toggle readable spacing"
                />
              </div>

              <div className="a11y-toggle-row">
                <span className="a11y-toggle-label">
                  <TextIcon />
                  Dyslexia Font
                </span>
                <Toggle
                  active={settings.dyslexiaFont}
                  onChange={() => handleToggle('dyslexiaFont')}
                  ariaLabel="Toggle dyslexia-friendly font"
                />
              </div>
            </div>

            {/* Cursor Size Section */}
            <div className="a11y-section">
              <div className="a11y-section-title">Cursor Size</div>
              <div className="a11y-size-selector">
                <SizeButton
                  size="normal"
                  label="Normal"
                  active={settings.cursorSize === 'normal'}
                  onClick={handleCursorSize}
                />
                <SizeButton
                  size="large"
                  label="Large"
                  active={settings.cursorSize === 'large'}
                  onClick={handleCursorSize}
                />
                <SizeButton
                  size="xl"
                  label="X-Large"
                  active={settings.cursorSize === 'xl'}
                  onClick={handleCursorSize}
                />
              </div>
            </div>

            {/* Keyboard Shortcuts */}
            <div className="a11y-shortcuts">
              <div className="a11y-shortcuts-title">Keyboard Shortcuts</div>
              <div className="a11y-shortcut">
                <span>Toggle Panel</span>
                <kbd>Alt + A</kbd>
              </div>
              <div className="a11y-shortcut">
                <span>High Contrast</span>
                <kbd>Alt + H</kbd>
              </div>
              <div className="a11y-shortcut">
                <span>Reduce Motion</span>
                <kbd>Alt + M</kbd>
              </div>
              <div className="a11y-shortcut">
                <span>Increase Text</span>
                <kbd>Alt + +</kbd>
              </div>
              <div className="a11y-shortcut">
                <span>Decrease Text</span>
                <kbd>Alt + -</kbd>
              </div>
            </div>

            {/* Reset Button */}
            <button
              className="a11y-reset-btn"
              onClick={handleReset}
              aria-label="Reset all accessibility settings to default"
            >
              Reset All Settings
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityAgent;
